import {
  debug as _debug,
  debugLabel as _debugLabel,
  getConsentSync,
  subscribeToConsent
} from '@client/core/utils/consent.js';
import { ConsentStatus } from '@schibsted-nmp/advertising-shared';

const debug = _debug.withSublabel(_debugLabel, 'Listener');

type ChangeCallback = (next: ConsentStatus) => void;

let isConsentListenerStarted = false;
let currentStatus: ConsentStatus | null = null;
const listeners = new Set<ChangeCallback>();

function toStatus(hasConsented: boolean): ConsentStatus {
  return hasConsented ? ConsentStatus.Accepted : ConsentStatus.Rejected;
}

function notifyConsentChange(newConsentStatus: ConsentStatus) {
  if (currentStatus === newConsentStatus) return;

  currentStatus = newConsentStatus;

  for (const consentCallback of Array.from(listeners)) {
    try {
      consentCallback(newConsentStatus);
    } catch (error) {
      debug.message('Failed notifying consent change!', error).error();
    }
  }
}

export function onConsentChange(callback: ChangeCallback): () => void {
  listeners.add(callback);
  if (currentStatus !== null) callback(currentStatus);
  return () => listeners.delete(callback);
}

export function startConsentListener(onChange: ChangeCallback): void {
  if (isConsentListenerStarted) {
    onConsentChange(onChange);
    return;
  }
  isConsentListenerStarted = true;

  try {
    const syncConsentStatus = getConsentSync();

    if (syncConsentStatus !== null) {
      notifyConsentChange(toStatus(syncConsentStatus));
    }
  } catch (error) {
    debug.message('Failed getting sync consent status!', error).error();
  }

  try {
    subscribeToConsent((updatedConsentValue) => {
      notifyConsentChange(toStatus(updatedConsentValue));
    });
  } catch (error) {
    debug.message('Failed subscribing to consent!', error).error();
  }

  onConsentChange(onChange);
}
